/**
 * Should this screen send POST /sessions/:uid/start? Decided from the
 * SERVER's session status; the per-tab `ev.start.called.<uid>` flag is only
 * a debounce for when the server cannot be asked.
 *
 * Since sw-ev-api #120 the API moves a session to 'preparing' only once its
 * RemoteStart has been sent, so:
 *   - pending / authorized       → nothing sent yet: start. A flag left by an
 *     earlier page load is stale (its /start never reached the server) and
 *     must not veto this one. Re-sending is safe: the API's start claim is
 *     idempotent (one RemoteStart per session).
 *   - preparing / starting / …   → a start is already in flight: do not send.
 *   - terminal                   → nothing to start.
 *   - unknown (probe failed)     → honour the flag if present, else start.
 */
const START_NEEDED = new Set(['pending', 'authorized', 'created', 'payment_authorized']);
const START_IN_FLIGHT = new Set([
  'preparing', 'starting', 'remote_start_requested', 'waiting_for_start',
  'active', 'started', 'charging', 'stopping',
]);
const TERMINAL = new Set(['completed', 'finalized', 'stopped', 'failed', 'voided', 'expired', 'cancelled']);

export const startCalledKey = (sessionUid) => `ev.start.called.${sessionUid}`;

export function readStartFlag(sessionUid) {
  try { return Boolean(sessionStorage.getItem(startCalledKey(sessionUid))); } catch { return false; }
}
export function writeStartFlag(sessionUid) {
  try { sessionStorage.setItem(startCalledKey(sessionUid), Date.now().toString()); } catch { /* best effort */ }
}
export function clearStartFlag(sessionUid) {
  try { sessionStorage.removeItem(startCalledKey(sessionUid)); } catch { /* best effort */ }
}

/**
 * @returns {{ action: 'start' } | { action: 'skip', reason: 'backend_in_flight'|'backend_terminal'|'flag_present' }}
 */
export function decideStart({ backendStatus, flagPresent }) {
  const s = String(backendStatus || '').toLowerCase();
  if (TERMINAL.has(s)) return { action: 'skip', reason: 'backend_terminal' };
  if (START_IN_FLIGHT.has(s)) return { action: 'skip', reason: 'backend_in_flight' };
  if (START_NEEDED.has(s)) return { action: 'start' };
  return flagPresent ? { action: 'skip', reason: 'flag_present' } : { action: 'start' };
}
